import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { captureEvent } from "../api/posthog";

const API_BASE = "https://api-v2.socialspectrum.org/api/web";
const APP_STORE_URL = "https://apps.apple.com/app/social-spectrum/id6473112455";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.social_spectrum.mobile";

function formatDate(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-AU", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
}

function formatTime(timeStr) {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString("en-AU", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}

export default function DeepLinkHandler() {
    const { id } = useParams();
    const { pathname } = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Determine type from path: /event/:id, /blog/:id, etc.
    const contentType = pathname.split("/")[1]; // "event", "blog", "article", or "podcast"

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;

    // Map content type to API endpoint and App scheme path
    const typeConfig = {
        event: {
            api: `get_event_public/${id}`,
            scheme: `socialspectrum://event/${id}`,
            label: "Event",
            titleKey: "event_name",
            imageKey: "image_url",
            detailKey: "event_detail"
        },
        blog: {
            api: `get_blog_public/${id}`,
            scheme: `socialspectrum://blog/${id}`,
            label: "Blog",
            titleKey: "blog_title",
            imageKey: "blog_image_url",
            detailKey: "blog_content"
        },
        article: {
            api: `get_article_public/${id}`,
            scheme: `socialspectrum://article/${id}`,
            label: "Article",
            titleKey: "title",
            imageKey: "image_url",
            detailKey: "description"
        },
        podcast: {
            api: `get_podcast_public/${id}`,
            scheme: `socialspectrum://podcast/${id}`,
            label: "Podcast",
            titleKey: "title",
            imageKey: "image_url",
            detailKey: "description"
        }
    };

    const config = typeConfig[contentType] || typeConfig.event;

    useEffect(() => {
        setLoading(true);
        setError(false);

        fetch(`${API_BASE}/${config.api}`)
            .then((res) => res.json())
            .then((resData) => {
                if (resData.status === 1) {
                    setData(resData.data);
                    if (contentType === 'event') {
                        captureEvent('event_viewed', { event_id: id });
                    }
                } else {
                    setError(true);
                }
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [id, config.api]);

    useEffect(() => {
        if (!isMobile || loading || error || !data) return;
        // Auto-redirect to app scheme
        window.location.href = config.scheme;
    }, [data, isMobile, loading, error, config.scheme]);

    const handleOpenApp = () => {
        captureEvent('signup_started', { source: 'deep_link_cta', type: contentType });
        window.location.href = config.scheme;

        // Fallback to store
        setTimeout(() => {
            if (isIOS) {
                window.plausible("App Store Click");
                window.location.href = APP_STORE_URL;
            } else if (isAndroid) {
                window.plausible("Play Store Click");
                window.location.href = PLAY_STORE_URL;
            }
        }, 2500);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.logo}>🌈</div>
                    <div style={styles.spinner}></div>
                    <p style={styles.loadingText}>Loading {config.label} details...</p>
                </div>
                <style>{spinnerCSS}</style>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.logo}>🌈</div>
                    <h2 style={styles.title}>{config.label} not found</h2>
                    <p style={styles.subtitle}>
                        This link might be invalid or has been removed.
                    </p>
                    <a href="https://socialspectrum.org" style={styles.primaryBtn}>
                        Visit Social Spectrum
                    </a>
                </div>
            </div>
        );
    }

    const title = data[config.titleKey];
    const image = data[config.imageKey];
    const details = data[config.detailKey] ? data[config.detailKey].replace(/<[^>]*>/g, "") : "";

    return (
        <div style={styles.container}>
            <style>{spinnerCSS}</style>
            <div style={styles.card}>
                <div style={styles.header}>
                    <div style={styles.logo}>🌈</div>
                    <span style={styles.appName}>Social Spectrum</span>
                </div>

                {image && (
                    <div style={styles.imageContainer}>
                        <img src={image} alt={title} style={styles.eventImage} />
                    </div>
                )}

                <div style={styles.eventInfo}>
                    <span style={styles.badge}>{config.label}</span>
                    <h1 style={styles.eventTitle}>{title}</h1>

                    {contentType === 'event' && (
                        <>
                            <div style={styles.metaRow}>
                                <span style={styles.metaIcon}>📅</span>
                                <span style={styles.metaText}>
                                    {formatDate(data.event_date)} at {formatTime(data.event_time)}
                                </span>
                            </div>
                            {data.event_location && (
                                <div style={styles.metaRow}>
                                    <span style={styles.metaIcon}>📍</span>
                                    <span style={styles.metaText}>{data.event_location}</span>
                                </div>
                            )}
                        </>
                    )}

                    {details && (
                        <p style={styles.description}>
                            {details.substring(0, 180)}
                            {details.length > 180 ? "..." : ""}
                        </p>
                    )}
                </div>

                <div style={styles.ctaSection}>
                    {isMobile ? (
                        <>
                            <button style={styles.primaryBtn} onClick={handleOpenApp}>
                                Open in App
                            </button>
                            <p style={styles.ctaSubtext}>Don't have the app yet?</p>
                        </>
                    ) : (
                        <p style={styles.desktopText}>
                            Scan the QR code or download the app to view this {config.label.toLowerCase()}
                        </p>
                    )}
                    <div style={styles.storeRow}>
                        <a href={APP_STORE_URL} style={styles.storeBtn} target="_blank" rel="noreferrer" onClick={() => window.plausible("App Store Click")}>
                            <span>🍎</span> App Store
                        </a>
                        <a href={PLAY_STORE_URL} style={styles.storeBtn} target="_blank" rel="noreferrer" onClick={() => window.plausible("Play Store Click")}>
                            <span>▶️</span> Google Play
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

const spinnerCSS = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const styles = {
    container: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #8CA0D8 0%, #6B7EC4 50%, #5468A8 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: "'Poppins', 'Segoe UI', sans-serif",
    },
    card: {
        background: "#ffffff",
        borderRadius: "24px",
        padding: "0 0 32px 0",
        maxWidth: "420px",
        width: "100%",
        boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        overflow: "hidden",
        animation: "fadeIn 0.4s ease",
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "20px 24px 16px",
        borderBottom: "1px solid #f0f0f0",
    },
    logo: { fontSize: "28px" },
    appName: { fontSize: "18px", fontWeight: "700", color: "#4A5C9A" },
    imageContainer: { width: "100%", height: "200px", overflow: "hidden" },
    eventImage: { width: "100%", height: "100%", objectFit: "cover" },
    eventInfo: { padding: "20px 24px" },
    badge: {
        display: "inline-block",
        background: "#EEF1FB",
        color: "#5468A8",
        fontSize: "11px",
        fontWeight: "600",
        padding: "4px 12px",
        borderRadius: "20px",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: "10px",
    },
    eventTitle: {
        fontSize: "22px",
        fontWeight: "700",
        color: "#1a1a2e",
        margin: "0 0 16px 0",
        lineHeight: "1.3",
    },
    metaRow: { display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "10px" },
    metaIcon: { fontSize: "16px", marginTop: "1px" },
    metaText: { fontSize: "14px", color: "#444", lineHeight: "1.4" },
    description: {
        fontSize: "14px",
        color: "#666",
        lineHeight: "1.6",
        marginTop: "14px",
        padding: "14px",
        background: "#f9f9ff",
        borderRadius: "12px",
        borderLeft: "3px solid #8CA0D8",
    },
    ctaSection: { padding: "0 24px", textAlign: "center" },
    primaryBtn: {
        display: "block",
        width: "100%",
        background: "linear-gradient(90deg, #8CA0D8, #5468A8)",
        color: "#fff",
        border: "none",
        borderRadius: "14px",
        padding: "15px 24px",
        fontSize: "16px",
        fontWeight: "700",
        cursor: "pointer",
        textDecoration: "none",
        textAlign: "center",
        marginBottom: "14px",
        boxSizing: "border-box",
    },
    ctaSubtext: { fontSize: "13px", color: "#888", marginBottom: "12px" },
    desktopText: { fontSize: "14px", color: "#666", marginBottom: "16px" },
    storeRow: { display: "flex", gap: "12px", justifyContent: "center" },
    storeBtn: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        background: "#1a1a2e",
        color: "#fff",
        padding: "10px 18px",
        borderRadius: "10px",
        fontSize: "13px",
        fontWeight: "600",
        textDecoration: "none",
        flex: 1,
        justifyContent: "center",
    },
    spinner: {
        width: "40px",
        height: "40px",
        border: "4px solid #EEF1FB",
        borderTop: "4px solid #8CA0D8",
        borderRadius: "50%",
        animation: "spin 0.9s linear infinite",
        margin: "20px auto",
    },
    loadingText: { color: "#888", fontSize: "14px", textAlign: "center" },
    title: { fontSize: "22px", fontWeight: "700", color: "#1a1a2e", marginBottom: "10px", textAlign: "center" },
    subtitle: { color: "#666", fontSize: "14px", textAlign: "center", marginBottom: "24px" },
};
